import { last, pick, isEmpty } from 'lodash';
import { ToastType } from '@honeybook/hbui/dist/components/HBToast/HBToast.types';
import type { RootStore } from '~/stores/root.store';
import {
    CoreInboundEvents,
    CoreOutboundEvents,
    GeneralOutboundEvents,
    InvoiceInboundEvents,
    InvoiceOutboundEvents
} from '~/components/Builder/Mobile/builderMobileApi.enums';
import type { TBlock, TServiceItemBlock } from '~/types/builder.types';
import { ChargeContainerBlock } from '~/models/blocks/charge-container-block.model';
import { ServicesBlock } from '~/models/blocks/services-block.model';
import type { InvoiceColumnHeaderBlock } from '~/models/blocks/invoice-column-header-block.model';
import type { InvoiceSummaryBlock } from '~/models/blocks/invoice-summary-block.model';
import {
    IMobileAppService,
    InvoiceSummaryMobileProps,
    MobileInvoiceBlockData,
    MobileInvoiceHeaderBlockEventData
} from '~/components/Builder/Mobile/builderMobileApi.types';
import {
    getServiceItemForMobile,
    getServicesBlockData,
    getSinglePaymentData,
    sendPaymentUpdateBlock,
    setPropsOnInvoiceBlock,
    setTaxesPropsOnSummaryBlock
} from '~/components/Builder/Mobile/builderMobileApi.utils';
import { InvoiceBlock } from '~/models/blocks/invoice-block.model';
import type { InvoiceHeaderBlock } from '~/models/blocks/invoice-header-block.model';
import { IInvoiceHeaderBlockStyles } from '~/types/block-styles.types';
import BaseAbstractBuilderApi from '~/components/Builder/Mobile/Entities/BaseAbstractBuilderApi';
import BuilderMobileCoreApiHandler from '~/components/Builder/Mobile/Entities/Core/BuilderMobileCoreApiHandler';
import {
    IMember,
    IPaymentVariablePendingMarkAsPaid,
    IPaymentVariablePendingMarkAsRefunded,
    IPaymentVariableValue,
    ITaxResponse,
    TESServiceOrPackage
} from '~/types/server.types';
import {
    CHARGE_BLOCK_PROPERTIES,
    MobileChargeBlock
} from '~/components/Builder/Block/ChargeBlock/ChargeBlockCommon';
import type { InvoiceServiceItemBlock } from '~/models/blocks/invoice-service-item-block.model';
import {
    IImage,
    IServiceTemplate,
    IInvoiceSummary,
    MobileServiceItemUnitOption,
    ServiceItemType,
    TContainerableBlockType
} from '~/types/model.types';
import { ServiceItemKeys } from '~/components/common/InvoiceBlock/ServiceItemBlock.utils';
import { ServiceItemScope } from '~/types/builder.types';
import type { PaymentBlock } from '~/models/blocks/payment-block.model';
import { formatDate } from '~/utils/date.utils';
import { ServerErrorType } from '~/utils/server-response.utils';
import { FlowRouteType } from '~/utils/flow.utils';
import {
    getDepositDate,
    getPaymentMethodType
} from '~/components/common/InvoiceBlock/invoice.utils';
import { PaymentScheduleBlock } from '~/models/blocks/payment-schedule-block.model';
import { BASE_64_REGEX } from '~/utils/regex';
import { ServiceItemBlock } from '~/models/blocks/service-item-block.model';
import { TOAST_IDS } from '~/common/flows.constants';
import { isURIEncoded } from '~/utils/text.utils';
import { trackError } from '~/services/logger/error.utils';
import { FlowMXContextValue } from '~/components/Builder/FlowMX.provider';

export class BuilderMobileInvoiceApiHandler extends BaseAbstractBuilderApi {
    builderMobileCoreApi: BuilderMobileCoreApiHandler;

    constructor(
        rootStore: RootStore,
        flowMXContext: FlowMXContextValue,
        MobileAppService: IMobileAppService,
        builderMobileCoreApi: BuilderMobileCoreApiHandler
    ) {
        super(rootStore, flowMXContext, MobileAppService);
        this.builderMobileCoreApi = builderMobileCoreApi;
        Object.values(InvoiceInboundEvents).forEach(invoiceInboundEvent => {
            this.subscribeToEvent(invoiceInboundEvent);
        });
    }

    // INBOUND
    [InvoiceInboundEvents.CreateInvoiceBlock]({
        siblingId
    }: {
        siblingId: string;
    }) {
        const {
            toastStore,
            builderUiStore: { selectedPage, selectedFlow },
            pageCtrl
        } = this.rootStore;
        if (selectedFlow.hasInvoiceBlock) {
            toastStore.add({
                type: ToastType.Error,
                text: `A Flow can only have one Invoice Block.`
            });
            return;
        }
        if (
            selectedPage.contractBlock ||
            selectedPage.serviceSelectionBlocks.length
        ) {
            pageCtrl.createPageAndBlockAction({
                pageOptions: { pageTitle: 'Invoice' },
                blockType: 'invoice_container_block',
                blockOptions: {
                    invoiceToast:
                        'Great! We added a page with an invoice and a page with payment. When your client will make selections they will automatically be updated'
                }
            });
        } else {
            const siblingBlock = siblingId
                ? selectedFlow.draftRevision.blocksMap[siblingId]
                : (last(selectedPage.rootBlock.childBlocks) as TBlock);
            this.rootStore.blockCtrl.createBlock({
                block: siblingBlock,
                blockType: 'invoice_container_block',
                direction: 'bottom'
            });
        }

        this.builderMobileCoreApi[CoreOutboundEvents.PageLoaded]();
    }

    [InvoiceInboundEvents.CreatePaymentBlock]() {
        const {
            blockCtrl,
            builderUiStore: { selectedFlow, selectedBlock }
        } = this.rootStore;

        const sendPaymentUpdateCallback = () => {
            sendPaymentUpdateBlock(
                selectedFlow,
                selectedBlock,
                this.MobileAppService
            );
        };

        blockCtrl.createPaymentBlock({
            paymentScheduleBlock: selectedFlow.paymentScheduleBlock,
            applyResolvedCallback: () => sendPaymentUpdateCallback()
        });
    }

    [InvoiceInboundEvents.UpdateInvoiceHeaderBlock]({
        block
    }: {
        block: InvoiceHeaderBlock;
    }) {
        const {
            builderUiStore: { selectedFlow, selectedBlock },
            blockCtrl
        } = this.rootStore;
        const oldBlock = selectedFlow.draftRevision.blocksMap[
            block.block_id
        ] as TBlock;

        blockCtrl.updateBlock(oldBlock, block);

        const blockData: MobileInvoiceHeaderBlockEventData = {
            ...oldBlock.getDataForServer()
        };

        blockData.billToUser = (selectedBlock as InvoiceHeaderBlock).billToUser;
        const { show_bill_to } =
            selectedBlock.styles as IInvoiceHeaderBlockStyles;
        blockData.isDisabledDropdown =
            !show_bill_to || !!selectedFlow.is_template;

        this.MobileAppService.triggerAppEvent(
            CoreOutboundEvents.UpdateBlockData,
            {
                block: blockData
            }
        );
    }

    [InvoiceInboundEvents.UpdateInvoiceAppearanceColumnsBlock]({
        block
    }: {
        block: InvoiceColumnHeaderBlock;
    }) {
        const {
            blockCtrl,
            builderUiStore: { selectedFlow, selectedBlockId },
            toastStore
        } = this.rootStore;
        const { invoiceCtrl } = this.flowMXContext;

        const oldInvoiceColumnsBlock = selectedFlow.draftRevision.blocksMap[
            block.block_id
        ] as InvoiceColumnHeaderBlock;

        const hasAppearanceFieldChanged =
            oldInvoiceColumnsBlock.styles.invoice_appearance !==
            block.styles.invoice_appearance;

        const hasShowImageFieldChanged =
            oldInvoiceColumnsBlock.styles.show_image !==
            block.styles.show_image;

        if (hasShowImageFieldChanged) {
            invoiceCtrl.updateInvoiceHeaderColumnAndServiceItems(
                oldInvoiceColumnsBlock,
                {
                    styles: { show_image: block.styles.show_image }
                }
            );
        } else {
            // update the invoice columns headers block with the styles
            blockCtrl.updateBlock(oldInvoiceColumnsBlock, {
                styles: block.styles
            });
        }

        const servicesBlock = selectedFlow.draftRevision.blocksMap[
            selectedBlockId
        ] as ServicesBlock;

        // create the ServicesBlock with the invoice summary and columns data
        const oldChargeBlock = (oldInvoiceColumnsBlock.parent as InvoiceBlock)
            .chargeBlock;
        const servicesBlockData = getServicesBlockData({
            servicesBlock,
            invoiceColumnsBlock: block,
            invoiceSummaryBlock: (oldInvoiceColumnsBlock.parent as InvoiceBlock)
                .invoiceSummaryBlock,
            chargeBlock: oldChargeBlock,
            isRecurringInvoice: (oldInvoiceColumnsBlock.parent as InvoiceBlock)
                .isRecurringInvoice,
            isRecurringUnder10Days: (
                oldInvoiceColumnsBlock.parent as InvoiceBlock
            ).isRecurringUnder10Days
        });

        this.MobileAppService.triggerAppEvent(
            CoreOutboundEvents.UpdateBlockData,
            {
                block: servicesBlockData
            }
        );

        if (
            hasAppearanceFieldChanged &&
            !toastStore.byId(TOAST_IDS.INVOICE_COLUMNS_UPDATED)
        ) {
            toastStore.add({
                id: TOAST_IDS.INVOICE_COLUMNS_UPDATED,
                type: ToastType.Info,
                text: 'The change you made took effect, but is only visible in a desktop.',
                autoDismissMs: 6000
            });
        }
    }

    [InvoiceInboundEvents.UpdateInvoiceAppearanceSummaryBlock]({
        block,
        origin = 'services_block',
        serviceItemBlockId
    }: {
        block: InvoiceSummaryBlock;
        origin: TContainerableBlockType;
        serviceItemBlockId?: string;
    }) {
        const {
            builderUiStore: {
                selectedFlow,
                selectedBlockId,
                showInvoiceOverlayForId
            }
        } = this.rootStore;

        if (origin === 'services_block') {
            const oldInvoiceSummaryBlock = selectedFlow.draftRevision.blocksMap[
                block.block_id
            ] as InvoiceSummaryBlock;

            // update the invoice summary block
            this.rootStore.blockCtrl.updateBlock(oldInvoiceSummaryBlock, {
                invoice_summary: (block as InvoiceSummaryBlock).invoice_summary
            });

            const servicesBlock = selectedFlow.draftRevision.blocksMap[
                selectedBlockId
            ] as ServicesBlock;

            // create the ServicesBlock with the invoice summary and columns data
            const servicesBlockData = getServicesBlockData({
                servicesBlock,
                invoiceColumnsBlock: (
                    oldInvoiceSummaryBlock.parent as InvoiceBlock
                ).invoiceColumnHeaderBlock,
                invoiceSummaryBlock: block as InvoiceSummaryBlock,
                chargeBlock: (oldInvoiceSummaryBlock.parent as InvoiceBlock)
                    .chargeBlock,
                isRecurringInvoice: (
                    oldInvoiceSummaryBlock.parent as InvoiceBlock
                ).isRecurringInvoice,
                isRecurringUnder10Days: (
                    oldInvoiceSummaryBlock.parent as InvoiceBlock
                ).isRecurringUnder10Days
            });

            this.MobileAppService.triggerAppEvent(
                CoreOutboundEvents.UpdateBlockData,
                {
                    block: servicesBlockData
                }
            );
        }

        if (origin === 'invoice_container_block') {
            const invoiceSummaryBlock = selectedFlow.draftRevision.blocksMap[
                block.block_id
            ] as InvoiceSummaryBlock;

            // update the invoice summary block
            this.rootStore.blockCtrl.updateBlock(invoiceSummaryBlock, {
                invoice_summary: (block as InvoiceSummaryBlock).invoice_summary
            });

            const updatedSummaryBlock = selectedFlow.draftRevision.blocksMap[
                block.block_id
            ] as InvoiceSummaryBlock;

            const invoiceBlock = updatedSummaryBlock.invoiceBlock;
            const blockData: MobileInvoiceBlockData = {
                ...invoiceBlock.getDataForServer()
            };

            setPropsOnInvoiceBlock(
                blockData as MobileInvoiceBlockData,
                showInvoiceOverlayForId,
                invoiceBlock,
                selectedFlow
            );

            this.MobileAppService.triggerAppEvent(
                CoreOutboundEvents.UpdateBlockData,
                {
                    block: blockData
                }
            );

            if (serviceItemBlockId) {
                // called when editing taxes from "apply taxes"
                const invoiceItemBlock = selectedFlow.draftRevision.blocksMap[
                    serviceItemBlockId
                ] as ServiceItemBlock;

                this[InvoiceOutboundEvents.ServiceTaxesClicked]({
                    blockId: serviceItemBlockId,
                    taxes: invoiceItemBlock.taxValuesMap
                });
            }
        }
    }

    [InvoiceInboundEvents.UpdatePaymentBlock]({
        blockId,
        blockData
    }: {
        blockId: string;
        blockData: Record<string, any>;
    }) {
        const {
            builderUiStore: { selectedFlow, selectedBlock }
        } = this.rootStore;

        const oldPaymentBlock = selectedFlow.draftRevision.blocksMap[
            blockId
        ] as PaymentBlock;

        const paymentBlockData = {
            ...oldPaymentBlock.payment_content,
            fixed_date:
                oldPaymentBlock.payment_content?.fixed_date ||
                formatDate(new Date(), 'YYYY-MM-dd')
        };

        const sendPaymentUpdateCallback = () => {
            sendPaymentUpdateBlock(
                selectedFlow,
                selectedBlock,
                this.MobileAppService
            );
        };

        try {
            this.rootStore.blockCtrl.updateBlock(
                oldPaymentBlock,
                {
                    payment_content: {
                        ...paymentBlockData,
                        ...blockData
                    }
                },
                {
                    applyResolvedCallback: () => sendPaymentUpdateCallback()
                }
            );
        } catch (error) {
            this.MobileAppService.triggerAppEvent(
                GeneralOutboundEvents.ServerError,
                {
                    type: ServerErrorType.PaymentUpdateError,
                    message: 'Error updating the payment',
                    reason:
                        error?.data?.error_message ||
                        'Error updating the payment'
                }
            );
        }
    }

    async [InvoiceInboundEvents.MarkAsPaid]({
        paymentBlockId,
        paymentData,
        origin
    }: {
        paymentBlockId: string;
        paymentData: IPaymentVariablePendingMarkAsPaid;
        origin: FlowRouteType;
    }) {
        const {
            builderUiStore: { selectedFlow },
            flowCtrl,
            variableCtrl,
            toastStore
        } = this.rootStore;
        const paymentBlock = selectedFlow.draftRevision.blocksMap[
            paymentBlockId
        ] as PaymentBlock;
        let status = 'success';

        try {
            if (origin === FlowRouteType.responses) {
                const response = await flowCtrl.markAsPaid(
                    paymentData,
                    selectedFlow,
                    paymentBlock.paymentId
                );
                status = response.status;
            } else {
                const sendPaymentUpdateCallback = () => {
                    sendPaymentUpdateBlock(
                        selectedFlow,
                        paymentBlock.paymentScheduleBlock,
                        this.MobileAppService
                    );
                };
                variableCtrl.updateVariable<Partial<IPaymentVariableValue>>(
                    selectedFlow,
                    paymentBlock.connectedVariable,
                    {
                        pending_mark_as_paid: paymentData
                    },
                    {
                        applyResolvedCallback: result => {
                            if (result.status === 'success') {
                                selectedFlow.setWasMarkedAsPaidInCurrentSession();
                            }
                            sendPaymentUpdateCallback();
                        }
                    }
                );
            }
        } catch (error) {
            toastStore.add({
                type: ToastType.Error,
                text: `Payment was not marked as paid.`,
                autoDismissMs: 6000
            });
            if (origin === FlowRouteType.edit) {
                this.MobileAppService.triggerAppEvent(
                    GeneralOutboundEvents.ServerError,
                    {
                        type: ServerErrorType.MAPError,
                        reason: error?.data?.error_message,
                        message: 'Payment was not marked as paid.'
                    }
                );
            }
        } finally {
            if (origin === FlowRouteType.responses) {
                this.MobileAppService.triggerAppEvent(
                    GeneralOutboundEvents.UpdateInResponsesDone,
                    {
                        status
                    }
                );
            }
        }
    }

    async [InvoiceInboundEvents.MarkAsRefunded]({
        paymentBlockId,
        paymentData,
        origin
    }: {
        paymentBlockId: string;
        paymentData: IPaymentVariablePendingMarkAsRefunded;
        origin: FlowRouteType;
    }) {
        const {
            builderUiStore: { selectedFlow },
            variableCtrl,
            flowCtrl,
            userStore: { currUser },
            toastStore
        } = this.rootStore;
        const paymentBlock = selectedFlow.draftRevision.blocksMap[
            paymentBlockId
        ] as PaymentBlock;
        let status = 'success';
        const enrichedPaymentData: IPaymentVariablePendingMarkAsRefunded = {
            ...paymentData,
            refunding_user_id: currUser._id
        };
        try {
            if (origin === FlowRouteType.responses) {
                const response = await flowCtrl.markAsRefund(
                    enrichedPaymentData,
                    selectedFlow,
                    paymentBlock.paymentId
                );
                status = response.status;
            } else {
                const sendPaymentUpdateCallback = () => {
                    sendPaymentUpdateBlock(
                        selectedFlow,
                        paymentBlock.paymentScheduleBlock,
                        this.MobileAppService
                    );
                    toastStore.add({
                        type: ToastType.Success,
                        text: `Payment was marked as refunded.`
                    });
                };
                // consider refactoring this updateVariable function to reuse same logic
                variableCtrl.updateVariable<Partial<IPaymentVariableValue>>(
                    selectedFlow,
                    paymentBlock.connectedVariable,
                    {
                        pending_mark_as_refunded: enrichedPaymentData
                    },
                    { applyResolvedCallback: sendPaymentUpdateCallback }
                );
            }
        } catch (error) {
            toastStore.add({
                type: ToastType.Error,
                text: `Payment was not marked as refunded. Please try again.`,
                autoDismissMs: 6000
            });
            if (origin === FlowRouteType.edit) {
                this.MobileAppService.triggerAppEvent(
                    GeneralOutboundEvents.ServerError,
                    {
                        type: ServerErrorType.RefundedError,
                        reason: error?.data?.error_message,
                        message:
                            'Payment was not marked as refunded. Please try again.'
                    }
                );
            }
        } finally {
            if (origin === FlowRouteType.responses) {
                this.MobileAppService.triggerAppEvent(
                    GeneralOutboundEvents.UpdateInResponsesDone,
                    {
                        status
                    }
                );
            }
        }
    }

    async [InvoiceInboundEvents.UndoMarkAsPaid]({
        paymentBlockId,
        origin
    }: {
        paymentBlockId: string;
        origin: FlowRouteType;
    }) {
        const {
            builderUiStore: { selectedBlock, selectedFlow },
            variableCtrl,
            flowCtrl,
            toastStore
        } = this.rootStore;

        const paymentBlock = this.rootStore.builderUiStore.selectedFlow
            .draftRevision.blocksMap[paymentBlockId] as PaymentBlock;
        let status = 'success';
        try {
            if (origin === FlowRouteType.edit) {
                const sendPaymentUpdateCallback = () => {
                    sendPaymentUpdateBlock(
                        selectedFlow,
                        selectedBlock,
                        this.MobileAppService
                    );
                };
                variableCtrl.updateVariable<Partial<IPaymentVariableValue>>(
                    selectedFlow,
                    paymentBlock.connectedVariable,
                    {
                        pending_undo_mark_as_paid: true,
                        pending_mark_as_paid: null
                    },
                    { applyResolvedCallback: sendPaymentUpdateCallback }
                );
            } else {
                const response = await flowCtrl.undoMarkAsPaid(
                    selectedFlow,
                    paymentBlock.paymentId
                );
                status = response.status;
            }
        } catch (error) {
            toastStore.add({
                type: ToastType.Error,
                text: `Payment was not marked as unpaid. Please try again.`,
                autoDismissMs: 6000
            });
            if (origin === FlowRouteType.edit) {
                this.MobileAppService.triggerAppEvent(
                    GeneralOutboundEvents.ServerError,
                    {
                        type: ServerErrorType.UndoMAPError,
                        reason: error?.data?.error_message,
                        message:
                            'Payment was not marked as unpaid. Please try again.'
                    }
                );
            }
        } finally {
            if (origin === FlowRouteType.responses) {
                this.MobileAppService.triggerAppEvent(
                    GeneralOutboundEvents.UpdateInResponsesDone,
                    {
                        status
                    }
                );
            }
        }
    }

    async [InvoiceInboundEvents.SendPaymentReminder]({
        paymentBlockId,
        origin
    }: {
        paymentBlockId: string;
        origin: FlowRouteType;
    }) {
        const {
            flowCtrl,
            toastStore,
            builderUiStore: { selectedBlock, selectedFlow }
        } = this.rootStore;
        const paymentBlock = this.rootStore.builderUiStore.selectedFlow
            .draftRevision.blocksMap[paymentBlockId] as PaymentBlock;
        try {
            await flowCtrl.sendReminder({
                flow: paymentBlock.flow,
                paymentId: paymentBlock.paymentId
            });
            toastStore.add({
                type: ToastType.Success,
                text: `Payment reminder was sent.`
            });
        } catch (_) {
            toastStore.add({
                type: ToastType.Error,
                text: `Error sending payment reminder.`
            });
        }
        if (origin === FlowRouteType.edit) {
            sendPaymentUpdateBlock(
                selectedFlow,
                selectedBlock,
                this.MobileAppService
            );
        }
    }

    [InvoiceInboundEvents.ViewPaymentReceipt]({
        paymentBlockId
    }: {
        paymentBlockId: string;
    }) {
        const {
            builderUiStore: { selectedFlow },
            userStore: { currUser }
        } = this.rootStore;
        const paymentBlock = selectedFlow.draftRevision.blocksMap[
            paymentBlockId
        ] as PaymentBlock;
        const invoiceBlock = paymentBlock.invoiceBlock as InvoiceBlock;
        const data = {
            fullName: currUser.full_name,
            companyName: selectedFlow.company?.company_name,
            companyTypeName: selectedFlow.company?.company_type_name,
            phoneNumber: currUser.phone_number,
            email: currUser.email,
            billTo: (invoiceBlock.invoiceHeaderBlock as InvoiceHeaderBlock)
                ?.billToContent,
            projectName: selectedFlow.project?.event_name,
            type: selectedFlow.project?.event_type,
            date: selectedFlow.getProjectDate(),
            time: selectedFlow.getProjectTime(),
            location: selectedFlow.project?.event_location || 'TBD',
            invoiceId: paymentBlock.invoiceId
                ? `#${paymentBlock.invoiceId}`
                : '',
            invoiceAmount: paymentBlock.paymentAmount,
            gratuity: paymentBlock.tipPaid,
            currency: selectedFlow.currency,
            total: paymentBlock.paymentAmount + paymentBlock.tipPaid,
            chargeDate: formatDate(
                paymentBlock.paymentChargeDate,
                'MM D, YYYY'
            ),
            dueDate: formatDate(paymentBlock.paymentDueDate, 'MM D, YYYY'),
            paymentMethodType: getPaymentMethodType(paymentBlock),
            refundedAmount: paymentBlock.refundedAmount,
            numberOfPaymentsPaid: invoiceBlock.numberOfPaymentsPaid,
            title: selectedFlow.title
        };
        this.MobileAppService.triggerAppEvent(
            InvoiceOutboundEvents.PaymentReceiptData,
            {
                data
            }
        );
    }

    [InvoiceInboundEvents.ViewPaymentDetails]({
        paymentBlockId
    }: {
        paymentBlockId: string;
    }) {
        const {
            builderUiStore: { selectedFlow }
        } = this.rootStore;
        const paymentBlock = selectedFlow.draftRevision.blocksMap[
            paymentBlockId
        ] as PaymentBlock;
        const invoiceBlock = paymentBlock.invoiceBlock as InvoiceBlock;
        const paymentScheduleBlock =
            invoiceBlock.invoicePaymentScheduleBlock as PaymentScheduleBlock;
        const { isRecurringInvoice } = paymentScheduleBlock;

        const data = {
            paymentStatus: paymentBlock.paymentStatus,
            paymentIndex: paymentBlock.paymentIndex,
            isRecurringInvoice,
            payerName: paymentBlock.paymentPayerName,
            numOfPaymentsOfTotal: paymentBlock.totalPaymentsCount,
            paymentAmount: paymentBlock.paymentAmount,
            currency: selectedFlow.currency,
            paymentId: paymentBlock.invoiceId,
            methodType: getPaymentMethodType(paymentBlock),
            isAutoPay: paymentBlock.isAutoPay,
            isPaid: paymentBlock.isPaid,
            gratuity: paymentBlock.tipPaid,
            autoPayStatus: paymentBlock.invoiceBlock.autoPayStatus,
            autoPayClientName: paymentBlock.flow.autoPayClientName,
            dueDate: formatDate(paymentBlock.paymentDueDate, 'MM D, YYYY'),
            chargeDate: formatDate(
                paymentBlock.paymentChargeDate,
                'MM D, YYYY'
            ),
            chargeText:
                'Payment method fees & repayments (per their repayment schedule) will be deducted. The balance will be deposited in your bank account.',
            depositDate: getDepositDate({
                depositDate: paymentBlock.depositDate,
                isManualPaid: paymentBlock.isManualPaid
            }),
            reminders: isEmpty(paymentBlock.reminders)
                ? null
                : paymentBlock.reminders.map(x => x.sent_on),
            chargeNotes: paymentBlock.chargeNotes || 'No notes'
        };
        this.MobileAppService.triggerAppEvent(
            InvoiceOutboundEvents.PaymentDetailsData,
            {
                data
            }
        );
    }

    [InvoiceInboundEvents.InvoiceServicesCreateNew]({
        blockId,
        name
    }: {
        blockId: string;
        name: string;
    }) {
        const {
            blockCtrl,
            builderUiStore: { selectedFlow }
        } = this.rootStore;
        const block = selectedFlow.draftRevision.blocksMap[
            blockId
        ] as TServiceItemBlock;
        // decode the base64 service name
        // the "if" if here for backwards compatibility,
        // since older versions may still keep it plain
        const title = BASE_64_REGEX.test(name) ? window.atob(name) : name;
        const serviceItem = {
            ...pick(block.service_item, ServiceItemKeys),
            title
        };
        blockCtrl.updateInvoiceServiceItemBlock({
            block,
            serviceItem,
            scope: ServiceItemScope.Invoice
        });
    }

    async [InvoiceInboundEvents.InvoiceServicesCreateFromExisting]({
        blockId,
        item
    }: {
        blockId: string;
        item: TESServiceOrPackage;
    }) {
        const {
            builderUiStore: { selectedFlow }
        } = this.rootStore;
        const { invoiceCtrl } = this.flowMXContext;
        const block = selectedFlow.draftRevision.blocksMap[
            blockId
        ] as TServiceItemBlock;
        if (block.isInPackage && item.type === ServiceItemType.Package) {
            throw new Error(
                'exception - package cannot be added into a package'
            );
        }
        // decode the base64 encoded rich html text from the native (all fields are encoded)
        // the "ifs" if here for backwards compatibility,
        // since older versions may still keep it plain
        const decodedItem = { ...item };
        try {
            // Decode the base64 encoded rich HTML text from the native
            // and update the new object
            if (BASE_64_REGEX.test(decodedItem.description)) {
                decodedItem.description = window.atob(decodedItem.description);
                if (isURIEncoded(decodedItem.description)) {
                    decodedItem.description = window.decodeURIComponent(
                        decodedItem.description
                    );
                }
            }
            if (BASE_64_REGEX.test(decodedItem.name)) {
                decodedItem.name = window.atob(decodedItem.name);
                if (isURIEncoded(decodedItem.name)) {
                    decodedItem.name = window.decodeURIComponent(
                        decodedItem.name
                    );
                }
            }
            if (BASE_64_REGEX.test(decodedItem.project_name)) {
                decodedItem.project_name = window.atob(
                    decodedItem.project_name
                );
                if (isURIEncoded(decodedItem.project_name)) {
                    decodedItem.project_name = window.decodeURIComponent(
                        decodedItem.project_name
                    );
                }
            }

            await invoiceCtrl.updateServiceBlockFromAutocomplete(
                block,
                decodedItem, // Use the new object here
                ServiceItemScope.Invoice
            );
        } catch (error) {
            trackError({
                error,
                message: 'Error update InvoiceServicesCreateFromExisting'
            });
        }
    }

    async [InvoiceInboundEvents.InvoiceServicesCreateFromHub]({
        blockId,
        serviceTemplateId
    }: {
        blockId: string;
        serviceTemplateId: IServiceTemplate['_id'];
    }) {
        const {
            builderUiStore: { selectedFlow },
            companyStore: { currCompany },
            serviceTemplatesService
        } = this.rootStore;
        const { invoiceCtrl } = this.flowMXContext;
        const block = selectedFlow.draftRevision.blocksMap[
            blockId
        ] as TServiceItemBlock;
        try {
            const serviceTemplate =
                await serviceTemplatesService.fetchServiceTemplate(
                    serviceTemplateId,
                    currCompany?._id
                );

            await invoiceCtrl.updateServiceBlockFromTemplateSelection(
                block,
                serviceTemplate,
                ServiceItemScope.Invoice
            );
        } catch (error) {
            trackError({
                error,
                message: 'Error update InvoiceServicesCreateFromHub'
            });
        }
    }

    [InvoiceInboundEvents.UpdateChargeBlock]({
        key,
        value,
        origin = 'charge_container_block'
    }: {
        key: string;
        value: any;
        origin: string;
    }) {
        const {
            builderUiStore: {
                selectedBlock,
                selectedFlow,
                showInvoiceOverlayForId
            }
        } = this.rootStore;
        const dataToUpdate = { [key]: value };

        if (origin === 'charge_container_block') {
            const {
                chargeBlock,
                flow: {
                    invoiceContainerBlock: {
                        isRecurringInvoice,
                        isRecurringUnder10Days
                    }
                }
            } = selectedBlock as ChargeContainerBlock;

            this.rootStore.blockCtrl.updateBlock(chargeBlock, dataToUpdate);
            const chargeProperties = {
                ...(pick(
                    (selectedBlock as ChargeContainerBlock).chargeBlock,
                    CHARGE_BLOCK_PROPERTIES
                ) as MobileChargeBlock),
                ...{
                    isRecurring: isRecurringInvoice,
                    isRecurringUnderMinimumDays: isRecurringUnder10Days
                }
            };

            const blockData = {
                ...selectedBlock.getDataForServer(),
                chargeBlock: chargeProperties
            };

            this.MobileAppService.triggerAppEvent(
                CoreOutboundEvents.UpdateBlockData,
                {
                    block: blockData
                }
            );
        } else if (origin === 'invoice_container_block') {
            const block = selectedFlow.invoiceContainerBlock;
            this.rootStore.blockCtrl.updateBlock(
                block.chargeBlock,
                dataToUpdate
            );

            const blockData: MobileInvoiceBlockData = {
                ...block.getDataForServer()
            };

            setPropsOnInvoiceBlock(
                blockData as MobileInvoiceBlockData,
                showInvoiceOverlayForId,
                block as InvoiceBlock,
                selectedFlow
            );
            this.MobileAppService.triggerAppEvent(
                CoreOutboundEvents.UpdateBlockData,
                {
                    block: blockData
                }
            );
        }
    }

    [InvoiceInboundEvents.UpdateContact]({
        userId,
        isNewMember,
        member
    }: {
        userId: string;
        isNewMember: boolean;
        member?: IMember;
    }) {
        const {
            variableCtrl,
            builderUiStore: { selectedBlock, selectedFlow, selectedWorkspace }
        } = this.rootStore;
        if (isNewMember) {
            selectedWorkspace.addMember(member);
            selectedFlow.addMember(member);
        }
        variableCtrl.updateContact(userId);
        const blockData: MobileInvoiceHeaderBlockEventData =
            selectedBlock.getDataForServer();

        blockData.billToUser = (selectedBlock as InvoiceHeaderBlock).billToUser;
        const { show_bill_to } =
            selectedBlock.styles as IInvoiceHeaderBlockStyles;
        blockData.isDisabledDropdown =
            !show_bill_to || selectedFlow.is_template;
        this.MobileAppService.triggerAppEvent(
            CoreOutboundEvents.UpdateBlockData,
            {
                block: blockData
            }
        );
    }

    [InvoiceInboundEvents.OnUnitSelection]({
        blockId,
        unit
    }: {
        blockId: string;
        unit: MobileServiceItemUnitOption;
    }) {
        const {
            blockCtrl,
            builderUiStore: { selectedFlow }
        } = this.rootStore;
        const block = selectedFlow.draftRevision.blocksMap[
            blockId
        ] as TServiceItemBlock;

        const serviceItem = getServiceItemForMobile(block);

        blockCtrl.updateServiceItemBlock(block, {
            ...serviceItem,
            unit: unit.value
        });
    }

    [InvoiceInboundEvents.OnUpdateTax]({
        blockId,
        taxId,
        value
    }: {
        blockId: string;
        taxId: string;
        value: boolean;
    }) {
        const {
            blockCtrl,
            builderUiStore: { selectedFlow }
        } = this.rootStore;
        const block = selectedFlow.draftRevision.blocksMap[
            blockId
        ] as TServiceItemBlock;

        blockCtrl.updateSingleServiceItemField({
            field: taxId,
            value,
            block
        });
    }

    [InvoiceInboundEvents.GoToInvoiceDisplay]({
        isInput
    }: {
        isInput: boolean;
    }) {
        const {
            flowCtrl,
            builderUiStore: { selectedFlow }
        } = this.rootStore;
        const servicesBlock =
            selectedFlow.invoiceContainerBlock.invoiceServicesBlock;
        if (isInput) {
            this.builderMobileCoreApi[CoreInboundEvents.UnselectBlock]({
                shouldNotSelectRoot: false
            });
        }
        flowCtrl.selectBlock({ blockId: servicesBlock.block_id });
    }

    [InvoiceInboundEvents.CloseResponsesModal]({}: {}) {
        const {
            builderUiStore: { setIsResponsesMobileModalOpen }
        } = this.rootStore;
        setIsResponsesMobileModalOpen(false);
    }

    [InvoiceInboundEvents.UpdateServiceImage]({
        block
    }: {
        block: InvoiceServiceItemBlock & { image: IImage };
    }) {
        const {
            blockCtrl,
            builderUiStore: { selectedFlow }
        } = this.rootStore;
        const isCircleCrop = block.image?.crop_ratio === 'Circle';
        const updatedStyles = { border_radius: block.styles.border_radius };
        const serviceItemBlock = selectedFlow.draftRevision.blocksMap[
            block.block_id
        ] as InvoiceServiceItemBlock;

        const oldBlockData = { ...serviceItemBlock.getDataForServer() };

        if (isEmpty(block.image)) {
            blockCtrl.updateBlock(oldBlockData, {
                service_image: null
            });
            this.builderMobileCoreApi[CoreOutboundEvents.BlockSelected]({
                blockId: selectedFlow.invoiceContainerBlock.block_id,
                isMultiStageFinished: true
            });
        } else {
            if (isCircleCrop) {
                updatedStyles.border_radius = 100;
            } else if (updatedStyles.border_radius === 100) {
                updatedStyles.border_radius = 0;
            }

            blockCtrl.updateBlock(serviceItemBlock, {
                service_image: block.image,
                styles: {
                    ...block.styles,
                    ...updatedStyles
                }
            });
        }

        const editedBlockDataForMobile = {
            ...oldBlockData,
            styles: { ...block.styles, ...updatedStyles },
            image: block.image
        };
        delete editedBlockDataForMobile.service_image;

        this.MobileAppService.triggerAppEvent(
            CoreOutboundEvents.UpdateBlockData,
            {
                block: editedBlockDataForMobile
            }
        );
    }

    [InvoiceInboundEvents.UpdateClientPaymentPlan](
        invoiceSummary: IInvoiceSummary
    ) {
        this.rootStore.blockCtrl.updateInvoiceSummary(invoiceSummary);
    }

    // Outbound Events

    [InvoiceOutboundEvents.PayBlockTbdClicked]() {
        this.MobileAppService.triggerAppEvent(
            InvoiceOutboundEvents.PayBlockTbdClicked,
            {}
        );
    }

    [InvoiceOutboundEvents.ServiceTaxesClicked]({
        blockId,
        taxes
    }: {
        blockId: string;
        taxes: Partial<ITaxResponse & { hidden: boolean }>[];
    }) {
        const {
            builderUiStore: {
                selectedFlow,
                selectedFlow: {
                    invoiceContainerBlock: { invoiceSummaryBlock }
                }
            }
        } = this.rootStore;
        const summaryBlock = pick(
            invoiceSummaryBlock,
            InvoiceSummaryMobileProps
        );
        setTaxesPropsOnSummaryBlock({
            summaryBlock,
            selectedFlow
        });

        const isAllTaxesDisabled =
            !summaryBlock.invoice_summary?.tax_1?.active &&
            !summaryBlock.invoice_summary?.tax_2?.active &&
            !summaryBlock.invoice_summary?.tax_3?.active;
        if (isAllTaxesDisabled) {
            return;
        }
        this.MobileAppService.triggerAppEvent(
            InvoiceOutboundEvents.ServiceTaxesClicked,
            {
                blockId,
                invoiceSummaryBlock: summaryBlock,
                taxesOnItem: taxes
            }
        );
    }

    [InvoiceOutboundEvents.ServiceImageClicked]({
        block
    }: {
        block: TServiceItemBlock;
    }) {
        const blockData = {
            image: block.service_image ? block.service_image : {},
            block_id: block.block_id,
            styles: {
                border_radius: block.styles?.border_radius
                    ? block.styles?.border_radius
                    : 0,
                ...block.styles
            },
            type: block.type
        };
        this.MobileAppService.triggerAppEvent(
            InvoiceOutboundEvents.ServiceImageClicked,
            { block: blockData }
        );
    }

    [InvoiceOutboundEvents.OpenPaymentOptions]({
        paymentId,
        paymentScheduleBlock
    }: {
        paymentId: string;
        paymentScheduleBlock: PaymentScheduleBlock;
    }) {
        const {
            builderUiStore: { selectedFlow }
        } = this.rootStore;
        const { blockData } = getSinglePaymentData({
            paymentId,
            selectedFlow,
            paymentScheduleBlock
        });
        this.MobileAppService.triggerAppEvent(
            InvoiceOutboundEvents.OpenPaymentOptions,
            { blockData }
        );
    }

    [InvoiceOutboundEvents.ReorderItemsClicked]({
        items
    }: {
        items: InvoiceServiceItemBlock[];
    }) {
        this.MobileAppService.triggerAppEvent(
            InvoiceOutboundEvents.ReorderItemsClicked,
            { items }
        );
    }

    [InvoiceOutboundEvents.UnitClicked]({
        blockId,
        options,
        selected,
        isHidden
    }: {
        blockId: string;
        options: MobileServiceItemUnitOption[];
        selected: MobileServiceItemUnitOption | null;
        onSelectedEventName: string;
        isHidden: boolean;
    }) {
        this.MobileAppService.triggerAppEvent(
            CoreOutboundEvents.SelectClicked,
            {
                blockId,
                options,
                selected,
                onSelectedEventName: InvoiceInboundEvents.OnUnitSelection,
                isHidden
            }
        );
    }

    [InvoiceOutboundEvents.AddTax]() {
        const {
            builderUiStore: { selectedFlow }
        } = this.rootStore;
        const blockData = {
            ...selectedFlow.invoiceContainerBlock.invoiceSummaryBlock.getDataForServer()
        };
        this.MobileAppService.triggerAppEvent(InvoiceOutboundEvents.AddTax, {
            block: blockData
        });
    }

    [InvoiceOutboundEvents.AddDiscount]() {
        const {
            builderUiStore: { selectedFlow }
        } = this.rootStore;

        const blockData = {
            ...selectedFlow.invoiceContainerBlock.invoiceSummaryBlock.getDataForServer()
        };
        this.MobileAppService.triggerAppEvent(
            InvoiceOutboundEvents.AddDiscount,
            {
                block: blockData
            }
        );
    }

    [InvoiceOutboundEvents.PaymentActionError]() {
        this.MobileAppService.triggerAppEvent(
            InvoiceOutboundEvents.PaymentActionError,
            {}
        );
    }

    [InvoiceOutboundEvents.CreateNewInvoice]() {
        this.MobileAppService.triggerAppEvent(
            InvoiceOutboundEvents.CreateNewInvoice,
            {}
        );
    }

    [InvoiceOutboundEvents.ViewInvoiceModalClicked]() {
        this.MobileAppService.triggerAppEvent(
            InvoiceOutboundEvents.ViewInvoiceModalClicked,
            {}
        );
    }

    [InvoiceOutboundEvents.DeleteServiceItemClicked]({
        blockId,
        title
    }: {
        blockId: string;
        title: string;
    }) {
        const {
            builderUiStore: { selectedFlow }
        } = this.rootStore;
        // select the root invoice block to solve cases of focused input fields
        this.builderMobileCoreApi[CoreOutboundEvents.BlockSelected]({
            blockId: selectedFlow.invoiceContainerBlock.block_id,
            isMultiStageFinished: true
        });
        this.MobileAppService.triggerAppEvent(
            InvoiceOutboundEvents.DeleteServiceItemClicked,
            { blockId, title }
        );
    }

    [InvoiceOutboundEvents.AddItemPackageClicked]({
        blockId,
        type,
        packageName
    }: {
        blockId: string;
        type: ServiceItemType.Service | ServiceItemType.Package;
        packageName?: string;
    }) {
        this.MobileAppService.triggerAppEvent(
            InvoiceOutboundEvents.AddItemPackageClicked,
            {
                blockId,
                type,
                ...(packageName !== null ? { packageName } : {})
            }
        );
    }

    [InvoiceOutboundEvents.EditTax]({ taxId }: { taxId: string }) {
        const {
            builderUiStore: {
                selectedFlow,
                selectedFlow: {
                    invoiceContainerBlock: { invoiceSummaryBlock }
                }
            }
        } = this.rootStore;

        const summaryBlock = pick(
            invoiceSummaryBlock,
            InvoiceSummaryMobileProps
        );
        setTaxesPropsOnSummaryBlock({
            summaryBlock,
            selectedFlow
        });

        this.MobileAppService.triggerAppEvent(InvoiceOutboundEvents.EditTax, {
            selectedTax: invoiceSummaryBlock.getTax(taxId),
            block: summaryBlock
        });
    }

    [InvoiceOutboundEvents.EditDiscount]() {
        const {
            builderUiStore: { selectedFlow }
        } = this.rootStore;
        const blockData = {
            ...selectedFlow.invoiceContainerBlock.invoiceSummaryBlock.getDataForServer()
        };
        this.MobileAppService.triggerAppEvent(
            InvoiceOutboundEvents.EditDiscount,
            {
                block: blockData
            }
        );
    }
}
